import { copperButton } from '@/shared/ui/game-theme';
import { GameIcon } from '@/shared/ui/game-icon';
import { useEffect, useMemo, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import { buildWorldTerrainContext, townsApiToInfluences } from '@/modules/game-procedural/procedural';
import type { Town } from '@/game/types';
import { useT } from '@/i18n';
import {
  bakeTerrainSlice,
  drawTerrainFromCache,
  terrainCacheNeedsRebake,
  WORLD_UNITS_PER_PX,
  type TerrainSliceCache,
} from '@/modules/game-procedural/minimap-terrain';

interface MinimapProps {
  cameraControls?: ReactNode;
  heroX: number;
  heroY: number;
  towns: Town[];
  routeWaypoints: Array<{ x: number; y: number }> | null;
  roadNetwork?: Array<Array<{ x: number; y: number }>>;
}

type MapMode = 0 | 1 | 2;

const SIZE_SMALL = 120;
const SIZE_LARGE = 240;

// ---- Styles ----

const containerStyle: CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 8,
  zIndex: 40,
  userSelect: 'none',
};

const toggleBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  padding: '3px 8px',
  fontSize: 11,
  fontWeight: 700,
  color: 'rgba(255,255,255,0.55)',
  background: 'rgba(0,0,0,0.5)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
  letterSpacing: 0,
  WebkitTapHighlightColor: 'transparent',
  marginBottom: 2,
  marginLeft: 'auto',
  pointerEvents: 'auto',
  ...copperButton,
};

function canvasStyleForSize(px: number): CSSProperties {
  return {
    width: px,
    height: px,
    borderRadius: 3,
    border: '2px solid #9e8058',
    boxShadow: '0 2px 12px rgba(0,0,0,0.6)',
    display: 'block',
  };
}

function modeLabel(mode: MapMode, mapStr: string): string {
  if (mode === 0) return `${mapStr} \u25B6`;
  if (mode === 1) return `${mapStr} S`;
  return `${mapStr} L`;
}

// ---- Component ----

export function Minimap({ heroX, heroY, towns, routeWaypoints, roadNetwork = [], cameraControls }: MinimapProps) {
  const tr = useT();
  const [mode, setMode] = useState<MapMode>(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const terrainCacheRef = useRef<TerrainSliceCache | null>(null);

  const size = mode === 2 ? SIZE_LARGE : SIZE_SMALL;
  const collapsed = mode === 0;

  const routeKey = useMemo(
    () =>
      routeWaypoints && routeWaypoints.length >= 2
        ? routeWaypoints.map((p) => `${p.x},${p.y}`).join(';')
        : '',
    [routeWaypoints],
  );

  const townsKey = useMemo(
    () =>
      towns.length === 0
        ? ''
        : towns.map((t) => `${t.id}:${t.worldX}:${t.worldY}:${t.radius}:${t.biome}:${t.levelMin}`).join('|'),
    [towns],
  );

  const roadsKey = useMemo(
    () =>
      roadNetwork.length === 0
        ? ''
        : roadNetwork.map((poly) => poly.map((p) => `${p.x},${p.y}`).join(';')).join('|'),
    [roadNetwork],
  );

  const worldKey = `${townsKey}#${routeKey}#${roadsKey}`;

  const miniCtx = useMemo(() => {
    if (towns.length === 0) return null;
    const activeRoute =
      routeWaypoints && routeWaypoints.length >= 2 ? routeWaypoints : null;
    return buildWorldTerrainContext(townsApiToInfluences(towns), activeRoute, roadNetwork);
  }, [worldKey, towns, routeWaypoints, roadNetwork]);

  useEffect(() => {
    if (collapsed) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;

    let cache = terrainCacheRef.current;
    if (terrainCacheNeedsRebake(cache, w, h, heroX, heroY, worldKey)) {
      cache = {
        w,
        h,
        centerX: heroX,
        centerY: heroY,
        worldKey,
        canvas: bakeTerrainSlice(w, h, heroX, heroY, miniCtx),
      };
      terrainCacheRef.current = cache;
    }

    drawTerrainFromCache(ctx, cache!, heroX, heroY, w, h);

    const cx = w / 2;
    const cy = h / 2;
    const minDim = Math.min(w, h);
    const gridStep = Math.max(10, Math.round(minDim / 6));
    const fontLetter = `${Math.max(7, Math.round(minDim * 0.067))}px monospace`;
    const fontTown = `${Math.max(5, Math.round(minDim * 0.048))}px monospace`;
    const margin = Math.max(4, Math.round(minDim * 0.05));
    const heroR = Math.max(3, Math.round(minDim * 0.035));
    const glowR = Math.max(8, Math.round(minDim * 0.09));

    const unitsPerPx = WORLD_UNITS_PER_PX;

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
    ctx.lineWidth = 0.5;
    for (let gx = 0; gx < w; gx += gridStep) {
      ctx.beginPath();
      ctx.moveTo(gx, 0);
      ctx.lineTo(gx, h);
      ctx.stroke();
    }
    for (let gy = 0; gy < h; gy += gridStep) {
      ctx.beginPath();
      ctx.moveTo(0, gy);
      ctx.lineTo(w, gy);
      ctx.stroke();
    }

    for (const town of towns) {
      let px = cx + (town.worldX - heroX) / unitsPerPx;
      let py = cy + (town.worldY - heroY) / unitsPerPx;

      const clamped =
        px < margin || px > w - margin || py < margin || py > h - margin;
      px = Math.max(margin, Math.min(w - margin, px));
      py = Math.max(margin, Math.min(h - margin, py));

      const radius = clamped ? heroR * 0.95 : heroR + 1;
      ctx.beginPath();
      ctx.arc(px, py, radius, 0, Math.PI * 2);
      ctx.fillStyle = '#daa520';
      ctx.fill();
      ctx.strokeStyle = '#ffd700';
      ctx.lineWidth = 1.2;
      ctx.stroke();

      const letter = town.name.charAt(0).toUpperCase();
      ctx.fillStyle = '#000';
      ctx.font = `bold ${fontLetter}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(letter, px, py + 0.5);

      if (!clamped) {
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.font = fontTown;
        ctx.fillText(town.name, px, py + radius + Math.max(5, minDim * 0.04));
      }
    }

    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR);
    grad.addColorStop(0, 'rgba(0, 255, 255, 0.3)');
    grad.addColorStop(1, 'rgba(0, 255, 255, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(cx - glowR, cy - glowR, glowR * 2, glowR * 2);

    ctx.beginPath();
    ctx.arc(cx, cy, heroR, 0, Math.PI * 2);
    ctx.fillStyle = '#00ffff';
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }, [heroX, heroY, towns, collapsed, size, worldKey, miniCtx]);

  return (
    <div style={containerStyle} className="th-minimap">
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', marginBottom: 3 }}>
      {cameraControls}
      <button
        type="button"
        style={{ ...toggleBtnStyle, minHeight: cameraControls ? 34 : undefined, marginBottom: 0, marginLeft: 0 }}
        title="Карта: свернуть / малый / большой"
        onClick={() => setMode((m) => ((((m + 1) % 3) as MapMode)))}
      >
        <GameIcon name="map" size={14} />{modeLabel(mode, tr.map)}
      </button>
      </div>
      {!collapsed && (
        <canvas
          ref={canvasRef}
          width={size}
          height={size}
          style={canvasStyleForSize(size)}
        />
      )}
    </div>
  );
}
